import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME } from "../ApiProvider";
import { api } from "./api";
// Query keys
const USE_RETRIEVE_VENUE = "useRetrieveVenue";
const USE_RETRIEVE_VENUE_BY_GOOGLE_PLACE_ID = "useRetrieveVenueByGooglePlaceId";
const USE_AUTOCOMPLETE_VENUES = "useAutocompleteVenues";
const USE_SEARCH_VENUES = "useSearchVenues";
export const useRetrieveVenue = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_VENUE, id],
        queryFn: async () => {
            const { data } = await api.get(`/venues/${id}/`);
            return data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveVenueByGooglePlaceId = ({ googlePlaceId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_VENUE_BY_GOOGLE_PLACE_ID, googlePlaceId],
        queryFn: async () => {
            const { data } = await api.get(`/venues/by-google-place-id/`, {
                params: { google_place_id: googlePlaceId },
            });
            return data;
        },
        enabled: !!googlePlaceId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useAutocompleteVenues = ({ query, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_AUTOCOMPLETE_VENUES, query],
        queryFn: async () => {
            const { data } = await api.get("/venues/autocomplete/", {
                params: { query },
            });
            return data;
        },
        enabled: !!query && query.trim().length > 0 && enabled,
        staleTime: 30000, // Shorter stale time for autocomplete
        cacheTime: 60000, // Shorter cache time for autocomplete
    });
};
export const useSearchVenues = ({ query, lat, lng, radius, ordering = "relevance", enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_VENUES, query, lat, lng, radius, ordering],
        queryFn: async () => {
            const params = {};
            if (query)
                params.q = query;
            if (lat !== undefined)
                params.lat = lat;
            if (lng !== undefined)
                params.lng = lng;
            if (radius !== undefined)
                params.radius = radius;
            if (ordering)
                params.ordering = ordering;
            const { data } = await api.get("/search/venues/", { params });
            return data;
        },
        enabled,
        staleTime: 30000,
        cacheTime: 60000,
    });
};
export const useCreateVenue = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (venueData) => {
            return await api.post("/venues/", venueData);
        },
        onMutate: (data) => {
            onMutate?.(data);
        },
        onSuccess: (data) => {
            // Invalidate venue-related queries
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE]);
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE_BY_GOOGLE_PLACE_ID]);
            queryClient.invalidateQueries([USE_SEARCH_VENUES]);
            onSuccess?.(data);
        },
        onError,
    });
};
export const useUpdateVenue = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, venueData }) => {
            return await api.put(`/venues/manage/${id}/`, venueData);
        },
        onMutate: (data) => {
            onMutate?.(data);
        },
        onSuccess: (data, variables) => {
            // Invalidate and update specific venue queries
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE, variables.id]);
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE]);
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE_BY_GOOGLE_PLACE_ID]);
            onSuccess?.(data);
        },
        onError,
    });
};
export const useDeleteVenue = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id) => {
            await api.delete(`/venues/manage/${id}/`);
        },
        onMutate: (id) => {
            onMutate?.(id);
        },
        onSuccess: (data, id) => {
            // Invalidate venue-related queries
            queryClient.removeQueries([USE_RETRIEVE_VENUE, id]);
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE]);
            queryClient.invalidateQueries([USE_RETRIEVE_VENUE_BY_GOOGLE_PLACE_ID]);
            onSuccess?.();
        },
        onError,
    });
};
// Pitch Query Keys
const USE_LIST_PITCHES = "useListPitches";
export const useListPitches = ({ venueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_PITCHES, venueId],
        queryFn: async () => {
            const { data } = await api.get(`/venues/${venueId}/pitches/`);
            return data;
        },
        enabled: !!venueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreatePitch = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (pitchData) => {
            return await api.post("/pitches/", pitchData);
        },
        onMutate: (data) => {
            onMutate?.(data);
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries([USE_LIST_PITCHES, variables.venue]);
            onSuccess?.(data);
        },
        onError,
    });
};
export const useDeactivatePitch = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id) => {
            return await api.put(`/pitches/${id}/deactivate/`);
        },
        onMutate: (id) => {
            onMutate?.(id);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_PITCHES]);
            onSuccess?.(data);
        },
        onError,
    });
};
